import { CONSTANTS } from "src/constants";
import { COMMON_CONSTANTS } from "src/constants/commonConstants";
import { AdditionalInfoFormValues } from "src/models/office.model";
import {
  AdminUserQueryParams,
  GetAdminUsersParams,
  User,
} from "src/models/roleManagement.model";
import {
  ChangePasswordProps,
  LoginProps,
  ResetPasswordProps,
  ResetProps,
} from "src/models/user.model";
import commonUtils from "src/utils/common.utils";

import API_URL from "./apiConfig/api.config";
import handleResponse from "./apiConfig/api.response.service";
import apiCall from "./apiConfig/apiCall.config";

export async function addAdminUser(data?: User) {
  return await apiCall.post(API_URL.USER.ADD_ADMIN_USER, data);
}

export async function adminLogin(data: LoginProps) {
  return await apiCall.post(API_URL.USER.ADMIN_LOGIN, data);
}

export async function adminProfile() {
  return await apiCall.get(API_URL.USER.ADMIN_PROFILE);
}

export async function changePassword(data: ChangePasswordProps) {
  return await apiCall.patch(API_URL.USER.ADMIN_CHANGE_PASSWORD, data);
}
export async function contractOfficeUpload(
  uuid: string,
  contactId: string,
  payload: {
    contracts_details?: {
      [key: string]:
        | string
        | number
        | { [key: string]: string | number }
        | undefined;
    };
    file_path: string;
  }
) {
  const path = commonUtils.getUrlReplace(
    API_URL.USER.UPLOAD_CONTRACT_OFFICE,
    COMMON_CONSTANTS.CONTRACT_UUID,
    contactId
  );
  const url = commonUtils.getUrlReplace(
    path,
    COMMON_CONSTANTS.OFFICE_UUID,
    uuid
  );
  return await apiCall.patch(url, payload);
}
export async function contractProfessionalUpload(
  uuid: string,
  contactId: string,
  payload?: {
    contracts_details?: {
      completed_vaccination?: boolean;
      crime?: boolean;
      current_address_duration?: number;
      driver_license_number?: string;
      in_vaccination_process?: boolean;
      opted_out_vaccination?: boolean;
      previous_names?: string;
      ssn?: string;
      vaccinated_date?: Date | string;
    };
    file_path: string;
  }
) {
  const path = commonUtils.getUrlReplace(
    API_URL.USER.UPLOAD_CONTRACT_PROFESSIONAL,
    COMMON_CONSTANTS.CONTRACT_UUID,
    contactId
  );
  const url = commonUtils.getUrlReplace(
    path,
    COMMON_CONSTANTS.PROFESSIONAL_UUID,
    uuid
  );
  return await apiCall.patch(url, payload);
}
export async function editAdminUser(uuid: string, data?: User) {
  return await apiCall.patch(API_URL.USER.EDIT_ADMIN_USER + uuid, data);
}
export async function getAdminUser(params: AdminUserQueryParams) {
  return await apiCall.get(API_URL.USER.GET_ADMIN_USER, { ...params });
}

export async function getAdminUsers(params?: GetAdminUsersParams) {
  return await apiCall.get(API_URL.USER.GET_ADMIN_USERS, params);
}
export async function getAdminUserById(uuid: string) {
  return await apiCall.get(API_URL.USER.GET_ADMIN + uuid);
}
export async function getContactAdditionalInfo(uuid: string, officeId: string) {
  const idReplace = commonUtils.getUrlReplace(
    API_URL.USER.GET_ADDITIONAL_CONTACT_INFO,
    COMMON_CONSTANTS.CONTACT_UUID,
    uuid
  );
  const url = commonUtils.getUrlReplace(
    idReplace,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeId
  );
  return await apiCall.get(url);
}

export const getNotification = async () => {
  const res = await apiCall.get(API_URL.COMMON.NOTIFICATIONS);
  const responseData = await handleResponse(res);
  if (responseData.ok) {
    return responseData?.data;
  } else {
    return responseData;
  }
};
export async function getDocuments(type: string, uuid: string) {
  const url = commonUtils.getUrlReplace(
    type === CONSTANTS.DOCUMENT.OFFICE
      ? API_URL.USER.GET_OFFICE_DOCUMENTS
      : API_URL.USER.GET_PROFESSIONAL_DOCUMENTS,
    COMMON_CONSTANTS.UUID,
    uuid
  );
  return await apiCall.get(url);
}
export const getRefreshToken = async (token: string) => {
  const response = await apiCall.post(API_URL.AUTH.REFRESH, {
    refresh_token: token,
  });
  return response;
};
export async function resetEmail(data: ResetProps) {
  return await apiCall.post(API_URL.USER.RESET_EMAIL, data);
}
export async function resetPassword(data: ResetPasswordProps) {
  return await apiCall.post(API_URL.USER.RESET_PASSWORD, data);
}
export async function removeAdminUser(uuid: string) {
  const url = commonUtils.getUrlReplace(
    API_URL.USER.REMOVE_ADMIN_USER,
    COMMON_CONSTANTS.UUID,
    uuid
  );
  return await apiCall.patch(url);
}
export async function saveContactAdditionalInformation(
  data: AdditionalInfoFormValues,
  uuid: string,
  officeId: string
) {
  const idReplace = commonUtils.getUrlReplace(
    API_URL.USER.GET_ADDITIONAL_CONTACT_INFO,
    COMMON_CONSTANTS.CONTACT_UUID,
    uuid
  );
  const url = commonUtils.getUrlReplace(
    idReplace,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeId
  );

  return await apiCall.patch(url, data);
}
export async function saveOfficeAdditionalInformation(
  data: AdditionalInfoFormValues,
  uuid: string
) {
  const url = commonUtils.getUrlReplace(
    API_URL.USER.SAVE_ADDITIONAL_INFO,
    COMMON_CONSTANTS.UUID,
    uuid
  );
  return await apiCall.patch(url, data);
}

export const getRoleModules = async () => {
  const res = await apiCall.get(API_URL.USER.GET_ROLE_MODULES);
  const responseData = await handleResponse(res);
  if (responseData.ok) {
    return responseData?.data;
  } else {
    return responseData;
  }
};

export const getTimeZones = async () => {
  const res = await apiCall.get(API_URL.TIME_ZONE.TIMEZONE);
  const responseData = await handleResponse(res);
  if (responseData.ok) {
    return responseData?.data;
  } else {
    return responseData;
  }
};

export async function markAsInvalid(payload: { uuids: string[] }) {
  return await apiCall.patch(API_URL.COMMON.MARK_AS_INVALID, payload);
}
export async function prospectMarkAsInvalid(payload: { uuids: string[] }) {
  return await apiCall.patch(API_URL.COMMON.PROSPECT_MARK_AS_INVALID, payload);
}

export async function previewContract(type: string, uuid: string) {
  const url = commonUtils.getUrlReplace(
    type === CONSTANTS.DOCUMENT.OFFICE
      ? API_URL.USER.GET_OFFICE_PREVIEW
      : API_URL.USER.GET_PROFESSIONAL_PREVIEW,
    COMMON_CONSTANTS.UUID,
    uuid
  );
  return await apiCall.getBlobFile(url);
}
export const updateNotification = async (id?: string) => {
  const res = await apiCall.patch(API_URL.COMMON.NOTIFICATION + id);
  const responseData = await handleResponse(res);
  if (responseData.ok) {
    return responseData?.data;
  } else {
    return responseData;
  }
};
export async function validToken(token: string | null) {
  return await apiCall.get(API_URL.USER.VALID_TOKEN + token);
}
export async function updateContractOffice(
  uuid: string,
  payload: {
    contract_uuid: string[];
    release_contract: boolean;
  }
) {
  const url = commonUtils.getUrlReplace(
    API_URL.USER.RELEASE_CONTRACT_OFFICE,
    COMMON_CONSTANTS.UUID,
    uuid
  );
  return await apiCall.patch(url, payload);
}
export async function updateContractProfessional(
  uuid: string,
  payload: {
    contract_uuid: string[];
    release_contract: boolean;
  }
) {
  const url = commonUtils.getUrlReplace(
    API_URL.USER.RELEASE_CONTRACT_PROFESSIONAL,
    COMMON_CONSTANTS.UUID,
    uuid
  );
  return await apiCall.patch(url, payload);
}
export async function updatePaymentMethod(
  uuid: string,
  payload: { payment_method?: string; update?: boolean }
) {
  const url = commonUtils.getUrlReplace(
    API_URL.USER.UPDATE_PAYMENT_URL,
    COMMON_CONSTANTS.UUID,
    uuid
  );
  return await apiCall.patch(url, payload);
}
